import { useEffect, useRef, useState } from "react";
// import { fetchWarehouseDropdown } from "slices/Master/Warehouse/thunk";
import WarehouseAutocomplete from "Common/Components/InputComponent/WarehouseAutocomplete";
import ActionButton from "components/Buttons/ActionButton";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import ListingPageContainer from "components/PageContainer/ListingPageContainer";
import SearchBox from "components/SearchBox/SearchBox";
import ThemeTabs from "components/Tabs/ThemeTabs";
import useClearStateOnNavigation from "CustomHooks/useClearStateOnNavigation";
import usePermission from "CustomHooks/usePermission";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { app_modules } from "helpers/apps_modules_list";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchWarehouseDropdown } from "slices/Master/Warehouse/thunk";
import {
  exportGRNExcel,
  exportSaleOrderReturnExcel,
} from "slices/Warehouse/ItemInward2/GRN/thunk";
import { exportPendingLabourCSV } from "slices/Warehouse/ItemInward2/PendingLabourInward/thunk";
import { exportPendingStockTransferCSV } from "slices/Warehouse/ItemInward2/PendingStockTransfer/thunk";
import Swal from "sweetalert2";
import { showCustomSwal } from "utils/swalHelper";
import NoWarehousesMessage from "Common/Components/NoWarehousesMessage";
import GRN from "./GRN/GRN";
import PendingLabourInward from "./PendingLabourInward/PendingLabourInward";
import PendingSalesOrderReturn from "./PendingSalesOrderReturn/PendingSalesOrderReturn";
import PendingStockTransfer from "./PendingStockTransfer/PendingStockTransfer";

const ItemInwardContainer = () => {
  const dispatch = useDispatch<any>();
  const location = useLocation();
  const prevTabRef = useRef<number | null>(null);

  const [tab, setTab] = useState(
    Number(sessionStorage?.getItem("WarehouseItemInwardTab")) || 1
  );

  const [warehouseOptions, setWarehouseOptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // 🔥 Use preserved table state hook with tabIndex for per-tab state preservation
  const tableState = usePreservedTableState({
    key: "itemInward",
    tabIndex: tab,
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = tableState;

  const sortField = sorting[0]?.id ?? "";
  const sortDirection: "" | "asc" | "desc" | undefined = sorting[0]?.desc
    ? "desc"
    : sorting[0]
    ? "asc"
    : "";

  // 🔥 Warehouse search (optional) - stored separately as it's not part of table state
  const [warehouseIdSearch, setWarehouseIdSearch] = useState("");

  // Use custom hook to fetch and access permissions
  const { canView, canCreate } = usePermission(
    app_modules?.warehouse?.app_name,
    app_modules?.warehouse?.modules?.item_inward
  );

  // Clear state when navigating from create action (not edit)
  useClearStateOnNavigation(location, resetState);

  // ✅ Tab switch - reset state when tab changes
  const handleTabChange = (val: number) => {
    setTab(val);
    sessionStorage.setItem("WarehouseItemInwardTab", val.toString());
  };

  // Reset state when tab changes (using useEffect to handle tab changes)
  // This ensures state is cleared when switching tabs, but preserved when navigating away and back
  useEffect(() => {
    // Only reset if tab actually changed (not on initial mount)
    // The hook will have re-initialized with the new tabIndex by this point
    if (prevTabRef.current !== null && prevTabRef.current !== tab) {
      // Clear the state for the new tab
      resetState();
    }
    prevTabRef.current = tab;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]); // Only depend on tab, not resetState to avoid infinite loops

  // Tabs configuration
  const tabs = [
    { label: "Pending Stock Transfer" },
    { label: "Pending Labour Inward" },
    { label: "Pending Sales Order Return" },
    { label: "GRN" },
  ];

  // ✅ CSV Export
  const handleExport = async () => {
    // Show loading modal for all exports
    showCustomSwal({
      title: "Exporting...",
      text: "Please wait while we export your data.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      if (tab === 1) {
        // Pending Stock Transfer
        await exportPendingStockTransferCSV({
          sort: sortField,
          order: sortField ? (sortDirection as "asc" | "desc") : undefined,
          search,
          filters: filterValue,
          warehouse_id: warehouseIdSearch,
        });

        // Replace loading modal with success modal
        showCustomSwal({
          icon: "success",
          title: "Export Completed",
          text: "Export successful.",
        });
      } else if (tab === 2) {
        // Pending Labour Inward
        const { blob, filename } = await exportPendingLabourCSV({
          search,
          warehouse_id: warehouseIdSearch,
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? (sortDirection as "asc" | "desc") : undefined,
          filters: filterValue,
        });

        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Replace loading modal with success modal
        showCustomSwal({
          icon: "success",
          title: "Export Completed",
          text: "Export successful.",
        });
      } else if (tab === 3) {
        // Pending Sales Order Return
        const { blob, filename } = await exportSaleOrderReturnExcel({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? (sortDirection as "asc" | "desc") : undefined,
          search,
          filters: filterValue,
          warehouse_id: warehouseIdSearch,
        });

        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Replace loading modal with success modal
        showCustomSwal({
          icon: "success",
          title: "Export Completed",
          text: "Export successful.",
        });
      } else if (tab === 4) {
        // GRN
        const { blob, filename } = await exportGRNExcel({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? (sortDirection as "asc" | "desc") : undefined,
          search,
          filters: filterValue,
          warehouse_id: warehouseIdSearch,
        });

        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Replace loading modal with success modal
        showCustomSwal({
          icon: "success",
          title: "Export Completed",
          text: "Export successful.",
        });
      } else {
        // Invalid tab
        showCustomSwal({
          icon: "info",
          title: "Export Not Available",
          text: "Export functionality is not implemented yet for this tab.",
        });
      }
    } catch (error: any) {
      // Replace loading modal with error modal
      showCustomSwal({
        icon: "error",
        title: "Export Failed",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to export.",
      });
      console.error("Export error:", error);
    }
  };

  useEffect(() => {
    const fetchInit = async () => {
      try {
        setLoading(true);
        const { payload } = await dispatch(fetchWarehouseDropdown());
        setWarehouseOptions(payload.data);

        // Check if stored warehouse is still valid (exists in current options)
        const storedWarehouse = sessionStorage.getItem("WarehouseIdItemInward");
        const isStoredWarehouseValid = storedWarehouse &&
          payload.data.some((warehouse: any) => warehouse.warehouse_id === storedWarehouse);

        if (storedWarehouse && isStoredWarehouseValid) {
          setWarehouseIdSearch(storedWarehouse);
        } else {
          // Clear invalid stored warehouse and auto-select first available
          sessionStorage.removeItem("WarehouseIdItemInward");
          setWarehouseIdSearch(payload.data[0]?.warehouse_id || "");
          sessionStorage.setItem(
            "WarehouseIdItemInward",
            payload.data[0]?.warehouse_id || ""
          );
        }
      } catch (error) {
        toast.error("Failed to fetch initial data");
      } finally {
        setLoading(false);
      }
    };
    fetchInit();
  }, [dispatch]);

  // useEffect(() => {
  //   const fetchData = async () => {
  //     const { payload } = await dispatch(fetchWarehouseDropdown());
  //     setWarehouseOptions(payload.data);
  //     setWarehouseIdSearch(payload.data[0]?.warehouse_id || "");
  //   };
  //   fetchData();
  // }, [dispatch]);

  // ✅ Debounce search input to actual search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      const trimmedInput = searchInput.trim();

      const dateMatch = trimmedInput.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

      if (dateMatch) {
        const [, day, month, year] = dateMatch;
        const isoDate = `${year}-${month}-${day}`;
        setSearch(isoDate);
      } else {
        setSearch(trimmedInput);
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput, setSearch]);

  return (
    <ListingPageContainer>
      <Row>
        <Col lg={12}>
          <Card>
            <Card.Header>
              <Row className="align-items-center g-2">
                {/* Title */}
                <Col
                  xs={12}
                  sm={12}
                  md={12}
                  lg={2}
                  xl={2}
                  className="mb-2 mb-lg-0"
                >
                  <h6 className="card-title mb-0">Item Inward</h6>
                </Col>

                {/* Right-aligned elements container */}
                <Col
                  xs={12}
                  sm={12}
                  md={12}
                  lg={10}
                  xl={10}
                  className="mb-2 mb-lg-0"
                >
                  <Row className="g-2 justify-content-end align-items-center">
                    {/* Search Input Box */}
                    <Col xs={12} sm={12} md={6} lg="auto" xl="auto">
                      <SearchBox
                        value={searchInput}
                        onChange={setSearchInput}
                      />
                    </Col>

                    {/* Warehouse Autocomplete */}
                    <Col
                      xs={12}
                      sm={12}
                      md={6}
                      lg={4}
                      xl={4}
                      className=""
                      style={{ minWidth: "200px" }}
                    >
                      <WarehouseAutocomplete
                        options={warehouseOptions}
                        value={warehouseIdSearch}
                        loading={loading}
                        disableClearable
                        sessionStorageKey="WarehouseIdItemInward"
                        onChange={(warehouseId) => {
                          setWarehouseIdSearch(String(warehouseId));

                          // Reset filters and sorting when warehouse changes
                          setColumnFilters([]);
                          setFilterValue([]);
                          setSorting([]);
                          setPageIndex(0);
                        }}
                        showWarehouseType={true}
                      />
                    </Col>

                    {/* Buttons */}
                    <Col
                      xs={12}
                      sm={12}
                      md={12}
                      lg="auto"
                      xl="auto"
                      className="d-flex gap-2 align-items-center flex-nowrap justify-content-end justify-content-lg-start"
                    >
                      {canView && (
                        <ActionButton
                          variant="light"
                          label="Export"
                          icon="bi bi-box-arrow-up"
                          handleClick={handleExport}
                        />
                      )}

                      {/* Show Inward button only for GRN */}
                      {tab === 4 && canCreate && (
                        <ActionButton
                          label="Inward"
                          icon="bi bi-plus-circle"
                          navigateLink="/warehouse-item-inward-create-grn"
                          variant="primary"
                        />
                      )}
                    </Col>
                  </Row>
                </Col>
              </Row>

              <Row className="mt-3">
                {/* Tabs */}
                <Col xs={12}>
                  <ThemeTabs
                    tabs={tabs}
                    value={tab - 1}
                    onChange={(newValue) => handleTabChange(newValue + 1)}
                  />
                </Col>
              </Row>
            </Card.Header>

            <Card.Body>
              {warehouseOptions.length > 0 ? (
                <LoaderErrorWrapper loading={loading} error={null}>
                  {tab === 1 && (
                    <PendingStockTransfer
                      loading={loading}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      sorting={sorting}
                      columnFilters={columnFilters}
                      filterValue={filterValue}
                      search={search}
                      searchInput={searchInput}
                      sortField={sortField}
                      sortDirection={sortDirection}
                      warehouseIdSearch={warehouseIdSearch}
                      setPageIndex={setPageIndex}
                      setPageSize={setPageSize}
                      setSorting={setSorting}
                      setColumnFilters={setColumnFilters}
                      setFilterValue={setFilterValue}
                      setSearch={setSearch}
                      setSearchInput={setSearchInput}
                      setWarehouseIdSearch={setWarehouseIdSearch}
                    />
                  )}
                  {tab === 2 && (
                    <PendingLabourInward
                      loading={loading}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      sorting={sorting}
                      columnFilters={columnFilters}
                      filterValue={filterValue}
                      search={search}
                      searchInput={searchInput}
                      sortField={sortField}
                      sortDirection={sortDirection}
                      warehouseIdSearch={warehouseIdSearch}
                      setPageIndex={setPageIndex}
                      setPageSize={setPageSize}
                      setSorting={setSorting}
                      setColumnFilters={setColumnFilters}
                      setFilterValue={setFilterValue}
                      setSearch={setSearch}
                      setSearchInput={setSearchInput}
                      setWarehouseIdSearch={setWarehouseIdSearch}
                    />
                  )}
                  {tab === 3 && (
                    <PendingSalesOrderReturn
                      loading={loading}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      sorting={sorting}
                      columnFilters={columnFilters}
                      filterValue={filterValue}
                      search={search}
                      searchInput={searchInput}
                      sortField={sortField}
                      sortDirection={sortDirection}
                      warehouseIdSearch={warehouseIdSearch}
                      setPageIndex={setPageIndex}
                      setPageSize={setPageSize}
                      setSorting={setSorting}
                      setColumnFilters={setColumnFilters}
                      setFilterValue={setFilterValue}
                      setSearch={setSearch}
                      setSearchInput={setSearchInput}
                      setWarehouseIdSearch={setWarehouseIdSearch}
                    />
                  )}
                  {tab === 4 && (
                    <GRN
                      loading={loading}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      sorting={sorting}
                      columnFilters={columnFilters}
                      filterValue={filterValue}
                      search={search}
                      searchInput={searchInput}
                      sortField={sortField}
                      sortDirection={sortDirection}
                      warehouseIdSearch={warehouseIdSearch}
                      setPageIndex={setPageIndex}
                      setPageSize={setPageSize}
                      setSorting={setSorting}
                      setColumnFilters={setColumnFilters}
                      setFilterValue={setFilterValue}
                      setSearch={setSearch}
                      setSearchInput={setSearchInput}
                      setWarehouseIdSearch={setWarehouseIdSearch}
                    />
                  )}
                </LoaderErrorWrapper>
              ) : (
                <NoWarehousesMessage />
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </ListingPageContainer>
  );
};

export default ItemInwardContainer;